/**
 * SyncEngine - Bi-directional sync between IndexedDB and Supabase
 * 
 * Push: Local dirty records → Supabase
 * Pull: Supabase records (updated_at > cursor) → IndexedDB
 * Conflict: Server wins if server updated_at > local updated_at and local is dirty
 * 
 * React-agnostic singleton. UI subscribes via callbacks.
 */

import { supabase } from '@/integrations/supabase/client';
import { localDatabase, type LocalExpense, type LocalIncome } from './localDatabase';

export type SyncStatus = 'idle' | 'syncing' | 'error';

export interface SyncResult {
  pushed: number;
  pulled: number;
  conflicts: number;
  errors: number;
}

type SyncListener = (status: SyncStatus, pending: number) => void;
type SyncResultListener = (result: SyncResult) => void;
type ConflictListener = (info: { table: string; localId: string; serverUpdatedAt: string }) => void;

const SYNC_INTERVAL_MS = 30_000; // 30 seconds
const BATCH_SIZE = 50;
const LAST_SUCCESS_KEY = 'wren:sync:lastSuccessAt';
const STALE_AFTER_MS = 24 * 60 * 60 * 1000; // 24h
const MAX_BACKOFF_MS = 5 * 60 * 1000; // 5 min

class SyncEngine {
  private status: SyncStatus = 'idle';
  private pendingCount = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<SyncListener>();
  private resultListeners = new Set<SyncResultListener>();
  private conflictListeners = new Set<ConflictListener>();
  private userId: string | null = null;
  private initialized = false;
  private consecutiveFailures = 0;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resyncRequested = false;

  async initialize(userId: string): Promise<void> {
    this.userId = userId;

    // Migrate old localStorage queue on first run
    if (!this.initialized) {
      await localDatabase.migrateFromLocalStorage();
      await localDatabase.requestPersistentStorage();
      this.initialized = true;
    }

    // Update pending count
    await this.refreshPendingCount();

    // Start periodic sync
    this.startPeriodicSync();

    // Sync on visibility change (returning to app)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Sync when coming online
    window.addEventListener('online', this.handleOnline);
  }

  destroy(): void {
    this.stopPeriodicSync();
    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && navigator.onLine && this.userId) {
      this.sync();
    }
  };

  private handleOnline = () => {
    if (this.userId) {
      this.sync();
    }
  };

  private startPeriodicSync(): void {
    this.stopPeriodicSync();
    this.intervalId = setInterval(() => {
      if (navigator.onLine && this.userId && this.status !== 'syncing') {
        this.sync();
      }
    }, SYNC_INTERVAL_MS);
  }

  private stopPeriodicSync(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // ─── Public API ─────────────────────────────────────────

  getStatus(): SyncStatus {
    return this.status;
  }

  getPendingCount(): number {
    return this.pendingCount;
  }

  /** ISO timestamp of last fully-successful sync (no errors). null if never. */
  getLastSuccessAt(): string | null {
    try {
      return localStorage.getItem(LAST_SUCCESS_KEY);
    } catch {
      return null;
    }
  }

  /** Has a successful sync happened recently? */
  isStale(): boolean {
    const last = this.getLastSuccessAt();
    if (!last) return this.pendingCount > 0;
    return Date.now() - new Date(last).getTime() > STALE_AFTER_MS;
  }

  getConsecutiveFailures(): number {
    return this.consecutiveFailures;
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onSyncComplete(listener: SyncResultListener): () => void {
    this.resultListeners.add(listener);
    return () => this.resultListeners.delete(listener);
  }

  onConflict(listener: ConflictListener): () => void {
    this.conflictListeners.add(listener);
    return () => this.conflictListeners.delete(listener);
  }

  async sync(): Promise<SyncResult> {
    if (!this.userId || !navigator.onLine) {
      return { pushed: 0, pulled: 0, conflicts: 0, errors: 0 };
    }

    // If a save happens while a sync is already reading dirty rows, request
    // one immediate follow-up pass. This is especially important for chat
    // bulk saves where multiple rows can be added while the first push is in
    // flight; otherwise later rows wait for the 30s timer/focus event.
    if (this.status === 'syncing') {
      this.resyncRequested = true;
      return { pushed: 0, pulled: 0, conflicts: 0, errors: 0 };
    }

    const aggregate: SyncResult = { pushed: 0, pulled: 0, conflicts: 0, errors: 0 };

    do {
      this.resyncRequested = false;
      this.setStatus('syncing');

      const result: SyncResult = { pushed: 0, pulled: 0, conflicts: 0, errors: 0 };

      try {
        // Push first, then pull
        const pushResult = await this.push(this.userId);
        result.pushed = pushResult.pushed;
        result.errors = pushResult.errors;

        const pullResult = await this.pull(this.userId);
        result.pulled = pullResult.pulled;
        result.conflicts += pullResult.conflicts;

        this.setStatus('idle');
      } catch (err) {
        console.error('[SyncEngine] Sync error:', err);
        this.setStatus('error');
        result.errors++;
      }

      await this.refreshPendingCount();

      // Track success/failure & schedule retry on failure
      if (result.errors === 0) {
        this.consecutiveFailures = 0;
        try { localStorage.setItem(LAST_SUCCESS_KEY, new Date().toISOString()); } catch {}
        if (this.retryTimeoutId) {
          clearTimeout(this.retryTimeoutId);
          this.retryTimeoutId = null;
        }
      } else {
        this.consecutiveFailures++;
        this.scheduleRetry();
      }

      aggregate.pushed += result.pushed;
      aggregate.pulled += result.pulled;
      aggregate.conflicts += result.conflicts;
      aggregate.errors += result.errors;

      this.resultListeners.forEach(l => l(result));
    } while (this.resyncRequested && !!this.userId && navigator.onLine);

    return aggregate;
  }

  private scheduleRetry(): void {
    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
    // Exponential backoff: 2s, 4s, 8s ... capped at 5 min
    const delay = Math.min(2000 * Math.pow(2, this.consecutiveFailures - 1), MAX_BACKOFF_MS);
    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      if (navigator.onLine && this.userId) this.sync();
    }, delay);
  }

  // ─── Push: Local → Server ──────────────────────────────

  private async push(userId: string): Promise<{ pushed: number; errors: number }> {
    let pushed = 0;
    let errors = 0;

    // Push expenses
    const dirtyExpenses = await localDatabase.getUnsyncedExpenses(userId);
    for (const expense of dirtyExpenses) {
      try {
        if (expense._deleted) {
          const { error } = await supabase.from('expenses').delete().eq('id', expense.id);
          if (error && error.code !== 'PGRST116') { // Not found is ok for deletes
            throw error;
          }
          await localDatabase.removeExpensePermanently(expense.id);
          pushed++;
        } else {
          // Upsert: works for both new and updated records
          const { _dirty, _deleted, ...serverData } = expense;
          const { error } = await supabase.from('expenses').upsert(serverData as any, { onConflict: 'id' });
          if (error) throw error;
          await localDatabase.markExpenseSynced(expense.id);
          pushed++;
        }
      } catch (err) {
        console.error('[SyncEngine] Push expense error:', expense.id, err);
        errors++;
      }
    }

    // Push income
    const dirtyIncome = await localDatabase.getUnsyncedIncome(userId);
    for (const income of dirtyIncome) {
      try {
        if (income._deleted) {
          const { error } = await supabase.from('income').delete().eq('id', income.id);
          if (error && error.code !== 'PGRST116') throw error;
          await localDatabase.removeIncomePermanently(income.id);
          pushed++;
        } else {
          const { _dirty, _deleted, ...serverData } = income;
          const { error } = await supabase.from('income').upsert(serverData as any, { onConflict: 'id' });
          if (error) throw error;
          await localDatabase.markIncomeSynced(income.id);
          pushed++;
        }
      } catch (err) {
        console.error('[SyncEngine] Push income error:', income.id, err);
        errors++;
      }
    }

    return { pushed, errors };
  }

  // ─── Pull: Server → Local ─────────────────────────────

  private async pull(userId: string): Promise<{ pulled: number; conflicts: number }> {
    let pulled = 0;
    let conflicts = 0;

    // Pull expenses
    const expMeta = await localDatabase.getSyncMeta('expenses');
    const expCursor = expMeta?.lastPullCursor || '1970-01-01T00:00:00Z';

    const { data: serverExpenses, error: expError } = await supabase
      .from('expenses')
      .select('*')
      .eq('user_id', userId)
      .gt('updated_at', expCursor)
      .order('updated_at', { ascending: true })
      .limit(BATCH_SIZE);

    if (expError) {
      console.error('[SyncEngine] Pull expenses error:', expError);
    } else if (serverExpenses && serverExpenses.length > 0) {
      for (const serverExp of serverExpenses) {
        const local = await localDatabase.getExpenseById(serverExp.id);
        
        if (local && local._dirty) {
          // Conflict: local has unsaved changes AND server has newer data
          if (new Date(serverExp.updated_at) > new Date(local.updated_at)) {
            // Server wins — overwrite local
            await localDatabase.logSyncConflict({
              user_id: userId,
              table: 'expenses',
              row_id: serverExp.id,
              server_updated_at: serverExp.updated_at,
              local_updated_at: local.updated_at,
              local_snapshot: JSON.stringify(local),
            });
            await localDatabase.bulkPutExpenses([{
              ...serverExp,
              tag: serverExp.tag || null,
              _dirty: false,
              _deleted: false,
            } as LocalExpense]);
            conflicts++;
            this.conflictListeners.forEach(l => l({
              table: 'expenses',
              localId: serverExp.id,
              serverUpdatedAt: serverExp.updated_at,
            }));
          }
          // Otherwise local wins — will be pushed on next sync
        } else {
          // No conflict, just update local
          await localDatabase.bulkPutExpenses([{
            ...serverExp,
            tag: serverExp.tag || null,
            _dirty: false,
            _deleted: false,
          } as LocalExpense]);
          pulled++;
        }
      }

      // Update cursor
      const lastExp = serverExpenses[serverExpenses.length - 1];
      await localDatabase.setSyncMeta({
        id: 'expenses',
        lastPullCursor: lastExp.updated_at,
        lastPushAt: new Date().toISOString(),
      });
    }

    // Pull income
    const incMeta = await localDatabase.getSyncMeta('income');
    const incCursor = incMeta?.lastPullCursor || '1970-01-01T00:00:00Z';

    const { data: serverIncome, error: incError } = await supabase
      .from('income')
      .select('*')
      .eq('user_id', userId)
      .gt('updated_at', incCursor)
      .order('updated_at', { ascending: true })
      .limit(BATCH_SIZE);

    if (incError) {
      console.error('[SyncEngine] Pull income error:', incError);
    } else if (serverIncome && serverIncome.length > 0) {
      for (const serverInc of serverIncome) {
        const local = await localDatabase.getIncome(userId, serverInc.income_date, serverInc.income_date);
        const localMatch = local.find(l => l.id === serverInc.id);

        if (localMatch && localMatch._dirty) {
          if (new Date(serverInc.updated_at) > new Date(localMatch.updated_at)) {
            await localDatabase.logSyncConflict({
              user_id: userId,
              table: 'income',
              row_id: serverInc.id,
              server_updated_at: serverInc.updated_at,
              local_updated_at: localMatch.updated_at,
              local_snapshot: JSON.stringify(localMatch),
            });
            await localDatabase.bulkPutIncome([{
              ...serverInc,
              is_recurring: serverInc.is_recurring || false,
              _dirty: false,
              _deleted: false,
            } as LocalIncome]);
            conflicts++;
          }
        } else {
          await localDatabase.bulkPutIncome([{
            ...serverInc,
            is_recurring: serverInc.is_recurring || false,
            _dirty: false,
            _deleted: false,
          } as LocalIncome]);
          pulled++;
        }
      }

      const lastInc = serverIncome[serverIncome.length - 1];
      await localDatabase.setSyncMeta({
        id: 'income',
        lastPullCursor: lastInc.updated_at,
        lastPushAt: new Date().toISOString(),
      });
    }

    return { pulled, conflicts };
  }

  // ─── Helpers ────────────────────────────────────────────

  private async refreshPendingCount(): Promise<void> {
    if (!this.userId) return;
    const expenses = await localDatabase.getUnsyncedExpenses(this.userId);
    const income = await localDatabase.getUnsyncedIncome(this.userId);
    this.pendingCount = expenses.length + income.length;
    this.notify();
  }

  private setStatus(status: SyncStatus): void {
    this.status = status;
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach(l => l(this.status, this.pendingCount));
  }
}

// Singleton
export const syncEngine = new SyncEngine();
